import { useEffect, useState } from "react";
import { Clock, CheckCircle, Warning } from "@phosphor-icons/react";
import { deliveryDeadline, deliveryWindowHours } from "../lib/helpers";
import { money } from "../lib/helpers";

// Live 12h/24h delivery countdown from a shoot's start time.
export default function DeliveryCountdown({ p, compact = false }) {
  const [nowTs, setNowTs] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNowTs(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const deadline = deliveryDeadline(p);
  if (!deadline) return null;
  const hours = deliveryWindowHours(p);

  // Already delivered → show whether it beat the clock.
  if (p.deliverables_ready) {
    const readyAt = p.deliverables_ready_at ? new Date(p.deliverables_ready_at) : null;
    const onTime = readyAt ? readyAt <= deadline : null;
    const tone = onTime === false ? "#8A6D1B" : "#2e7d32";
    if (compact) {
      return (
        <span data-testid="countdown-delivered" className="inline-flex items-center gap-1 text-xs font-medium" style={{ color: tone }}>
          <CheckCircle size={13} weight="fill" />Delivered{onTime === false ? " · late" : " · on time"}
        </span>
      );
    }
    return (
      <div className="border rounded-md p-4" style={{ borderColor: `${tone}55`, background: `${tone}0F` }} data-testid="delivery-countdown">
        <div className="flex items-center gap-2">
          <CheckCircle size={16} weight="fill" color={tone} />
          <span className="text-sm font-medium" style={{ color: tone }}>
            Delivered {onTime === false ? "after the deadline" : "on time"}
          </span>
        </div>
      </div>
    );
  }

  const remainMs = deadline - nowTs;
  const overdue = remainMs < 0;
  const abs = Math.abs(remainMs);
  const h = Math.floor(abs / 3600000);
  const m = Math.floor((abs % 3600000) / 60000);
  const s = Math.floor((abs % 60000) / 1000);
  const label = `${h}h ${String(m).padStart(2, "0")}m ${String(s).padStart(2, "0")}s`;
  const tone = overdue ? "#E01E37" : remainMs < 2 * 3600000 ? "#B08A2E" : "#2e7d32";
  const deadlineStr = deadline.toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

  if (compact) {
    return (
      <span data-testid="countdown-compact" className="inline-flex items-center gap-1 text-xs font-mono-num font-medium" style={{ color: tone }}>
        <Clock size={13} weight="fill" />{overdue ? `Overdue ${label}` : `${label} left`}
      </span>
    );
  }

  return (
    <div className="border rounded-md p-4" style={{ borderColor: `${tone}55`, background: `${tone}0F` }} data-testid="delivery-countdown">
      <div className="flex items-center gap-2 mb-1">
        {overdue ? <Warning size={16} weight="fill" color={tone} /> : <Clock size={16} weight="fill" color={tone} />}
        <span className="text-sm font-medium" style={{ color: tone }}>{overdue ? "Delivery overdue" : "Time left to deliver"}</span>
      </div>
      <p className="font-display text-3xl tabular-nums" style={{ color: tone }} data-testid="countdown-value">{overdue ? `−${label}` : label}</p>
      <p className="text-xs text-[#5C5A58] mt-1">
        {p.edited ? "Editing" : "Raws only"} · {hours}h from the shoot start. Deadline {deadlineStr}. Submit before the clock runs out to earn on-time points.
      </p>
    </div>
  );
}
